import { useState } from 'react';
import assistantFabIcon from '../../../assets/assistant-fab-icon.png';

interface AssistantFabProps {
  open: boolean;
  /** Late mientras el asistente está respondiendo. */
  thinking: boolean;
  /**
   * Acoplado a la barra de control en vez de flotando en la esquina.
   *
   * Se usa mientras hay un WebView: el botón flotante caía justo encima de los flotantes del
   * sitio embebido y, al ser otro documento, no había `z-index` que los rescatara.
   */
  docked: boolean;
  onClick: () => void;
}

/** Tamaño del icono dentro de esa área de toque. */
const ICON_SIZE = { floating: 88, docked: 32 } as const;

/**
 * Área de toque del botón en cada sitio (invisible: ya no hay disco que la dibuje).
 *
 * Flotante iguala al icono para que todo lo que se ve sea también clicable —el `<img>`
 * no recibe eventos (`pointerEvents: 'none'`), así que si desbordara el botón, esa parte
 * dejaría de responder al clic—.
 *
 * Acoplado, 38 px: es lo que cabe con aire en la barra de control de 45 px, que es la más baja
 * de los dos huecos (la de la tabla es más alta). Más grande y desbordaría esa fila.
 */
const HIT_AREA = { floating: ICON_SIZE.floating, docked: 38 } as const;

/**
 * El botón del asistente, flotante o acoplado.
 *
 * Flotando va a `z-index: 45`, que es el hueco libre del portal: por encima del contenido (10)
 * y de la barra lateral (40), y por debajo de los diálogos (100+) y de los avisos (9999). Si un
 * modal se abre, el asistente queda debajo, que es lo correcto.
 *
 * Acoplado no compite por capas: se coloca dentro de la barra de control como un botón más.
 */
export function AssistantFab({ open, thinking, docked, onClick }: AssistantFabProps) {
  const [hovered, setHovered] = useState(false);
  const hitArea = docked ? HIT_AREA.docked : HIT_AREA.floating;
  const iconSize = docked ? ICON_SIZE.docked : ICON_SIZE.floating;

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-label={open ? 'Cerrar el asistente' : 'Abrir el asistente'}
      aria-expanded={open}
      title={open ? 'Cerrar el asistente' : 'Pregúntale a la IA de ClickPalm'}
      style={{
        // Acoplado no lleva posición propia: lo coloca el flex de la barra que lo contiene.
        ...(docked
          ? { position: 'relative' as const }
          : { position: 'fixed' as const, right: '1.5rem', bottom: '1.5rem', zIndex: 45 }),
        width: `${hitArea}px`,
        height: `${hitArea}px`,
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        // Sin disco detrás, la sombra es lo único que separa el icono del contenido de la
        // página que quede debajo, sea cual sea su color.
        filter: hovered
          ? 'drop-shadow(0 8px 18px rgba(0,0,0,0.28))'
          : 'drop-shadow(0 3px 10px rgba(0,0,0,0.2))',
        // Acoplado no se levanta: dentro de una fila de botones, un salto vertical descoloca
        // la línea en vez de sugerir relieve.
        transform: hovered && !docked ? 'translateY(-2px) scale(1.05)' : 'none',
        transition: 'filter 0.2s ease, transform 0.2s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {/* El halo sólo aparece mientras piensa: si latiera siempre, dejaría de significar nada. */}
      {thinking && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            borderRadius: '50%',
            border: '2px solid var(--color-assistant-outline)',
            animation: 'brainHalo 1.6s ease-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {open ? (
        <span
          className="material-symbols-outlined"
          aria-hidden="true"
          style={{ fontSize: docked ? '18px' : '26px', color: 'var(--color-assistant-outline)' }}
        >
          close
        </span>
      ) : (
        <img
          src={assistantFabIcon}
          alt=""
          aria-hidden="true"
          width={iconSize}
          height={iconSize}
          style={{ display: 'block', pointerEvents: 'none' }}
        />
      )}
    </button>
  );
}
